import { type IconType, Pill } from "@clipboard-health/ui-components";
import { type Theme } from "@mui/material";

import { type MissedPunchRequestStatus } from "../types";

export type PastShiftPillStatus = MissedPunchRequestStatus | "unverified";

interface PastShiftStatusPillProps {
  status: PastShiftPillStatus;
}

interface PillConfig {
  label: string;
  iconType: IconType;
  intent: "warning" | "error";
}

const PILL_CONFIG_BY_STATUS: Record<Exclude<PastShiftPillStatus, "none">, PillConfig> = {
  pending: {
    label: "Pending approval",
    iconType: "clock-filled",
    intent: "warning",
  },
  declined: {
    label: "Declined",
    iconType: "triangle-exclamation",
    intent: "error",
  },
  unverified: {
    label: "Unverified",
    iconType: "triangle-exclamation",
    intent: "error",
  },
} as const;

function getIntentColors(
  theme: Theme,
  intent: "warning" | "error"
): {
  backgroundColor: string | undefined;
  textColor: string;
  iconColor: string | undefined;
} {
  const intentPalette = theme.palette.intent?.[intent];

  return {
    backgroundColor: intentPalette?.background,
    textColor: intentPalette?.text ?? theme.palette.text.primary,
    iconColor: intentPalette?.icon,
  };
}

export function PastShiftStatusPill(props: PastShiftStatusPillProps) {
  const { status } = props;

  if (status === "none") {
    return null;
  }

  const { label, iconType, intent } = PILL_CONFIG_BY_STATUS[status];

  return (
    <Pill
      variant="filled"
      size="small"
      label={label}
      iconType={iconType}
      backgroundColor={(theme) => getIntentColors(theme, intent).backgroundColor}
      color={(theme) => getIntentColors(theme, intent).textColor}
      iconColor={(theme) => getIntentColors(theme, intent).iconColor}
    />
  );
}
